import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { WarningDialog } from './warning-dialog';
import { OtpDialog } from './otp-dialog';
import { NewPasswordDialog } from './new-password-dialog';
import { toast } from 'react-toastify';
import { sendOtpOperation } from '../../operations/send-otp-operation';
import { verifyOtpOperation } from '../../operations/verify-otp-operation';
import { updatePasswordOperation } from '../../operations/update-password-operation';
import { PasswordResetType } from '../../../../../../api/types/auth/reset-password-type';

interface ChangePasswordFlowProps {
  userEmail: string;
  userPhoneNumber: string;
  onSuccess?: () => void;
  autoStart?: boolean;
}

type FlowStep = 'idle' | 'warning' | 'otp' | 'newPassword';

export const ChangePasswordFlow: React.FC<ChangePasswordFlowProps> = ({
  userEmail,
  userPhoneNumber,
  onSuccess,
  autoStart = false,
}) => {
  const [currentStep, setCurrentStep] = useState<FlowStep>('idle');
  const [resetToken, setResetToken] = useState<string>('');
  const [resetType, setResetType] = useState<PasswordResetType>(
    PasswordResetType.PhoneNumber,
  );

  // Auto-start the flow if autoStart is true
  React.useEffect(() => {
    if (autoStart && currentStep === 'idle') {
      setCurrentStep('warning');
    }
  }, [autoStart, currentStep]);

  const handleInitiate = () => {
    setCurrentStep('warning');
  };

  const handleWarningConfirm = async (selectedResetType: PasswordResetType) => {
    try {
      setResetType(selectedResetType);
      const token = await sendOtpOperation(selectedResetType);
      setResetToken(token);
      setCurrentStep('otp');
      const destination =
        selectedResetType === PasswordResetType.PhoneNumber
          ? 'לטלפון הנייד'
          : 'לכתובת האימייל';
      toast.info(`קוד אימות נשלח ${destination}`);
    } catch (error) {
      toast.error('שגיאה בשליחת קוד אימות. נסה שוב מאוחר יותר');
      console.error('Error sending OTP:', error);
    }
  };

  const handleWarningCancel = () => {
    setCurrentStep('idle');
    setResetToken('');
  };

  const handleOtpVerify = async (otp: string) => {
    try {
      await verifyOtpOperation(resetToken, otp);
      setCurrentStep('newPassword');
    } catch (error) {
      toast.error('קוד אימות שגוי. נסה שוב');
      console.error('Error verifying OTP:', error);
    }
  };

  const handleOtpCancel = () => {
    setCurrentStep('idle');
    setResetToken('');
  };

  const handlePasswordSubmit = async (newPassword: string) => {
    try {
      await updatePasswordOperation(resetToken, newPassword);
      toast.success('הסיסמא שונתה בהצלחה!');
      setCurrentStep('idle');
      setResetToken(''); // Clear token after successful password change

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error('שגיאה בשינוי הסיסמא. נסה שוב מאוחר יותר');
      console.error('Error updating password:', error);
    }
  };

  const handlePasswordCancel = () => {
    setCurrentStep('idle');
    setResetToken('');
  };

  return (
    <>
      {!autoStart && (
        <Button
          label="שינוי סיסמא"
          icon="pi pi-key"
          onClick={handleInitiate}
          severity="secondary"
          outlined
          style={{
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '500',
          }}
        />
      )}

      <WarningDialog
        visible={currentStep === 'warning'}
        onConfirm={handleWarningConfirm}
        onCancel={handleWarningCancel}
      />

      <OtpDialog
        visible={currentStep === 'otp'}
        userEmail={userEmail}
        userPhoneNumber={userPhoneNumber}
        resetType={resetType}
        onVerify={handleOtpVerify}
        onCancel={handleOtpCancel}
      />

      <NewPasswordDialog
        visible={currentStep === 'newPassword'}
        onSubmit={handlePasswordSubmit}
        onCancel={handlePasswordCancel}
      />
    </>
  );
};
